import { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppContext, EMPTY_OBJECT } from 'commons-ui';
import useService from '../../utils/useService';
import { TOURNAMENT_ENVIRONMENT } from '../../utils/constants';

const useCreate = () => {
	const emptyCsvObject = { oldCsv: '', newCsv: '' };
	const [showLoader, setShowLoader] = useState(false);
	const [isDiffClose, setIsDiffClose] = useState(true);
	const [diffFormState, setDiffFormState] = useState(true);
	const [uploadFormState, setUploadFormState] = useState(false);
	const [statusFormState, setStatusFormState] = useState(false);
	const [statusLogs, setStatusLogsState] = useState('');
	const [csvDiffValue, setCsvDiffValue] = useState(emptyCsvObject);
	const { app, showAlert } = useContext(AppContext);
	const history = useHistory();
	const { requestCreateTournament, requestCsvDiff, getTournamentCsvStatus } = useService();

	const createTournament = (createdData: any) => {
		setShowLoader(true);
		const tournamentLogKey: string = `tournamentDevLog-${createdData.gameName}`;
		const tournamentDevLogFromLocal: string | null = window.localStorage.getItem(tournamentLogKey);
		const tournamentDevLog = tournamentDevLogFromLocal ? JSON.parse(tournamentDevLogFromLocal as string) : {};
		if (
			createdData.environment === TOURNAMENT_ENVIRONMENT.STAGING ||
			(createdData.environment === TOURNAMENT_ENVIRONMENT.PROD && tournamentDevLog?.status === 'SUCCESS')
		) {
			requestCreateTournament(app.appName, { ...createdData }).then(
				(res: any) => {
					if (res?.status !== 200 || !res?.data?.success) {
						showAlert(res?.data?.error || 'Error occured while creating tournament', { type: 'error' });
					} else {
						showAlert('Tournament csv uploaded successfully');
						window.localStorage.setItem(tournamentLogKey, JSON.stringify(res?.data?.data?.data));
					}
					setShowLoader(false);
				},
				(err: any) => {
					showAlert(`Error: ${err}`, { type: 'error' });
					setShowLoader(false);
				}
			);
		} else if (tournamentDevLog?.status !== 'SUCCESS' && tournamentDevLog.docId) {
			showAlert('Checking status, Please wait!');
			getTournamentCsvStatus({ app }, { docId: tournamentDevLog.docId, environment: TOURNAMENT_ENVIRONMENT.STAGING }).then(
				(res: any) => {
					if (res?.status !== 200 || !res?.data?.success) {
						showAlert(res?.data?.error || 'Error while checking dev tournament status', { type: 'error' });
						setShowLoader(false);
					} else {
						const resp = res?.data?.data;
						window.localStorage.setItem(tournamentLogKey, JSON.stringify({ status: resp.status, docId: resp.docId }));
						if (resp.status === 'SUCCESS') {
							showAlert('Dev test passed, uploading on production, please wait');
							requestCreateTournament(app.appName, { ...createdData }).then(
								(response: any) => {
									if (response?.status !== 200 || !response?.data?.success) {
										showAlert(response?.data?.error || 'Error occured while creating tournament', {
											type: 'error'
										});
									} else {
										showAlert('Tournament csv uploaded successfully');
										window.localStorage.removeItem(tournamentLogKey);
										history.goBack();
									}
									setShowLoader(false);
								},
								(err: any) => {
									showAlert(`Error: ${err}`, { type: 'error' });
									setShowLoader(false);
								}
							);
						} else {
							showAlert(`Dev test status: ${resp.status} please try again`);
							setShowLoader(false);
						}
					}
				},
				(err: any) => {
					showAlert(`Error: ${err}`, { type: 'error' });
					setShowLoader(false);
				}
			);
		} else {
			showAlert('Please test this CSV on dev environment first', { type: 'warning' });
			setShowLoader(false);
		}
	};

	const getTournamentDevStatus = (data: any) => {
		setShowLoader(true);
		const tournamentLogKey: string = `tournamentDevLog-${data.gameName}`;
		const tournamentDevLogFromLocal: string | null = window.localStorage.getItem(tournamentLogKey);
		const tournamentDevLog = tournamentDevLogFromLocal ? JSON.parse(tournamentDevLogFromLocal as string) : null;
		if (!tournamentDevLog) {
			showAlert('No deployment found');
			setShowLoader(false);
		} else if (tournamentDevLog.status === 'SUCCESS') {
			showAlert('Deployment succeded, please go ahead for prod deployment');
			setShowLoader(false);
		} else {
			getTournamentCsvStatus({ app }, { docId: tournamentDevLog.docId, environment: TOURNAMENT_ENVIRONMENT.STAGING }).then(
				(res: any) => {
					if (res?.status !== 200 || !res?.data?.success) {
						showAlert(res?.data?.error || 'Error while checking dev tournament status', { type: 'error' });
						setShowLoader(false);
					} else {
						const resp = res?.data?.data;
						window.localStorage.setItem(tournamentLogKey, JSON.stringify({ status: resp.status, docId: resp.docId }));
						if (resp.status === 'SUCCESS') {
							showAlert('Deployment succeded, please go ahead for prod deployment');
							setShowLoader(false);
						} else {
							showAlert(`Dev test status: ${resp.status} please try again`);
							setShowLoader(false);
							setStatusLogsState(resp?.logs?.steps ? resp?.logs?.steps.join('<br>') : '');
						}
					}
				},
				(err: any) => {
					showAlert(`Error: ${err}`, { type: 'error' });
					setShowLoader(false);
				}
			);
		}
	};

	const csvDiff = (data: any) => {
		setShowLoader(true);
		requestCsvDiff(app.appName, { ...data }).then(
			(res: any) => {
				if (res?.status !== 200 || !res?.data?.success) {
					showAlert(res?.data?.error || 'Error occured while getting diff', { type: 'error' });
					setShowLoader(false);
				} else {
					showAlert('Csv Diff loaded');
					setShowLoader(false);
					setCsvDiffValue(res?.data?.data || emptyCsvObject);
				}
			},
			(err: any) => {
				showAlert(`Error: ${err.response.data.error}`, { type: 'error' });
				setShowLoader(false);
			}
		);
	};

	const handleFormSubmit = (submittedData: any) => {
		createTournament(submittedData);
	};

	const handleCsvDiff = (submittedData: any) => {
		setIsDiffClose(false);
		setCsvDiffValue(emptyCsvObject);
		csvDiff(submittedData);
	};

	const handleTournamentStatusSubmit = (submittedData: any) => {
		getTournamentDevStatus(submittedData);
	};

	const handleFormCancel = () => {
		history.goBack();
	};

	const handleDiffDivClose = () => {
		setIsDiffClose(true);
	};

	const handleOpenDiffForm = () => {
		setDiffFormState(true);
		setUploadFormState(false);
		setStatusFormState(false);
		setStatusLogsState('');
	};

	const handleUploadForm = () => {
		setDiffFormState(false);
		setUploadFormState(true);
		setStatusFormState(false);
		setIsDiffClose(true);
		setStatusLogsState('');
	};

	const handleStatusForm = () => {
		setDiffFormState(false);
		setUploadFormState(false);
		setStatusFormState(true);
		setIsDiffClose(true);
		setStatusLogsState('');
	};

	return {
		data: EMPTY_OBJECT,
		showLoader,
		handleFormSubmit,
		handleFormCancel,
		setShowLoader,
		handleCsvDiff,
		csvDiffValue,
		handleDiffDivClose,
		isDiffClose,
		handleOpenDiffForm,
		handleUploadForm,
		handleStatusForm,
		diffFormState,
		uploadFormState,
		statusFormState,
		statusLogs,
		handleTournamentStatusSubmit
	};
};

export default useCreate;
